import { useCallback } from 'react';
import { format } from 'date-fns';
import { useAppContext } from '../contexts/AppContext';
import { useDemo } from '../contexts/DemoContext';
import { useUndoRedo } from './useUndoRedo';
import { readExifData } from '../utils/exifUtils';
import { normalizeDateTaken } from '../utils/dateTaken';
import { EXIF_FIELDS } from '../config/fieldDefinitions';

export function useFiles() {
  const { files, setFiles, selectedIndices, setSelectedIndices, primaryIndex, setPrimaryIndex } = useAppContext();
  const { isDemo } = useDemo();
  const { recordAction } = useUndoRedo();

  const addFiles = useCallback(async (fileList) => {
    const newFiles = await Promise.all(
      Array.from(fileList)
        .filter(file => file.type === 'image/jpeg')
        .map(async (file) => {
          const arrayBuffer = await file.arrayBuffer();
          const exifData = await readExifData(arrayBuffer, {
            fileName: file.name,
            mimeType: file.type,
          });
          const normalizedExifData = {
            ...exifData,
            dateTaken: normalizeDateTaken(exifData.dateTaken),
          };
          const modifiedExifData = {
            ...normalizedExifData,
            software: EXIF_FIELDS.software.formatForExif(normalizedExifData.software),
          };
          const hasChanges = Object.keys(modifiedExifData).some(
            (fieldId) => modifiedExifData[fieldId] !== normalizedExifData[fieldId],
          );

          const thumbnailUrl = URL.createObjectURL(file);

          return {
            file,
            name: file.name,
            path: file.path, // Preserve path for Electron direct saves
            size: file.size,
            lastModified: file.lastModified,
            arrayBuffer,
            thumbnailUrl,
            exifData: normalizedExifData,
            modifiedExifData,
            hasChanges,
          };
        })
    );

    setFiles(prev => [...prev, ...newFiles]);
  }, [setFiles]);

  const removeFiles = useCallback((indicesToRemove) => {
    // indicesToRemove must be a Set of indices
    if (!indicesToRemove || indicesToRemove.size === 0) return;
    if (isDemo) return;

    // 1. Update Files
    setFiles(prev => prev.filter((_, i) => !indicesToRemove.has(i)));

    // 2. Update Selection and Primary Index
    // We need to calculate shifts based on current files
    const newSelected = new Set();
    let shift = 0;

    for (let i = 0; i < files.length; i++) {
        if (indicesToRemove.has(i)) {
            shift++;
        } else {
            if (selectedIndices.has(i)) {
                newSelected.add(i - shift);
            }
        }
    }
    setSelectedIndices(newSelected);

    // 3. Update Primary Index
    if (primaryIndex !== -1) {
        if (indicesToRemove.has(primaryIndex)) {
            setPrimaryIndex(-1);
            // Optionally could try to find nearest neighbor, but -1 (none selected) is safe
        } else {
            let primaryShift = 0;
            for (let i = 0; i < primaryIndex; i++) {
                if (indicesToRemove.has(i)) primaryShift++;
            }
            setPrimaryIndex(primaryIndex - primaryShift);
        }
    }

  }, [files, isDemo, setFiles, selectedIndices, setSelectedIndices, primaryIndex, setPrimaryIndex]);

  const updateFileExif = useCallback((index, newExifData) => {
    setFiles(prev => prev.map((f, i) => {
      if (i === index) {
        return {
          ...f,
          modifiedExifData: newExifData,
          hasChanges: true
        };
      }
      return f;
    }));
  }, [setFiles]);

  const assignFilesDate = useCallback((fileIndices, newDate) => {
    // Deep copy files including modifiedExifData to preserve undo state
    const previousFiles = files.map(f => ({
      ...f,
      modifiedExifData: { ...f.modifiedExifData }
    }));

    // If newDate is null, clear the date. Otherwise format it
    const formattedDate = normalizeDateTaken(
      newDate ? format(newDate, "yyyy-MM-dd'T'HH:mm:ss") : null
    );

    setFiles(prev => prev.map((f, i) => {
      if (fileIndices.includes(i)) {
        return {
          ...f,
          modifiedExifData: {
            ...f.modifiedExifData,
            dateTaken: formattedDate
          },
          hasChanges: true
        };
      }
      return f;
    }));

    const fileNames = fileIndices.map(i => files[i]?.name || `File ${i}`).join(', ');
    const actionDescription = newDate
      ? `Assign date to ${fileIndices.length} file(s): ${fileNames}`
      : `Remove date from ${fileIndices.length} file(s): ${fileNames}`;

    recordAction(
      actionDescription,
      () => setFiles(previousFiles),
      () => assignFilesDate(fileIndices, newDate),
      fileIndices
    );
  }, [files, setFiles, recordAction]);

  const assignFilesDatesIndividually = useCallback((fileIndicesWithDates) => {
    // fileIndicesWithDates: Array of {index, date}

    // Deep copy files including modifiedExifData to preserve undo state
    const previousFiles = files.map(f => ({
      ...f,
      modifiedExifData: { ...f.modifiedExifData }
    }));

    setFiles(prev => prev.map((f, i) => {
      const update = fileIndicesWithDates.find(item => item.index === i);
      if (update) {
        const formattedDate = update.date
          ? normalizeDateTaken(format(update.date, "yyyy-MM-dd'T'HH:mm:ss"))
          : null;

        return {
          ...f,
          modifiedExifData: {
            ...f.modifiedExifData,
            dateTaken: formattedDate
          },
          hasChanges: true
        };
      }
      return f;
    }));

    const fileNames = fileIndicesWithDates
      .map(({ index }) => files[index]?.name || `File ${index}`)
      .join(', ');
    const actionDescription = `Assign times to ${fileIndicesWithDates.length} file(s) with offsets`;

    recordAction(
      actionDescription,
      () => setFiles(previousFiles),
      () => assignFilesDatesIndividually(fileIndicesWithDates),
      fileIndicesWithDates.map(item => item.index)
    );
  }, [files, setFiles, recordAction]);

  return {
    files,
    addFiles,
    removeFiles,
    updateFileExif,
    assignFilesDate,
    assignFilesDatesIndividually
  };
}
